import React, { useState } from 'react';
import { ApiService, DeviceMapping } from '../services/api';
import { CameraScanner } from './CameraScanner';
import { ArchiveDialog } from './ArchiveDialog';
import { googleDriveService } from '../services/googleDrive';

interface QueryFormProps {
  apiService: ApiService;
  environment: string;
}

export const QueryForm: React.FC<QueryFormProps> = ({ apiService, environment }) => {
  const [searchValue, setSearchValue] = useState('');
  const [queryType, setQueryType] = useState<'serial' | 'url'>('serial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DeviceMapping | null>(null);
  const [editing, setEditing] = useState(false);
  const [editImageCreated, setEditImageCreated] = useState(false);
  const [editEtchCompleted, setEditEtchCompleted] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [scannedData, setScannedData] = useState<{ qrUrl: string; serial: string } | null>(null);

  // No LDDL/charset rules on the query path - length is the only check, so mappings
  // created by any path can be looked up.

  const validateSerialNumber = (value: string): { isValid: boolean; error?: string } => {
    // Trim whitespace first
    const trimmed = value.trim();
    
    if (!trimmed) {
      return { isValid: false, error: 'Serial number is required' };
    }

    // 4-char serials come from the web/Cognito create path, 5-char ones from the
    // programmatic POST /v1/mappings path. Both are queryable.
    if (trimmed.length !== 4 && trimmed.length !== 5) {
      return { isValid: false, error: 'Serial number must be 4 or 5 characters long' };
    }

    return { isValid: true };
  };

  const validateUrl = (url: string, environment: string): { isValid: boolean; error?: string } => {
    // Trim whitespace first
    const trimmed = url.trim();
    
    if (!trimmed) {
      return { isValid: false, error: 'URL is required' };
    }

    // Environment-specific base URLs
    const environmentUrls: Record<string, string> = {
      'production': 'https://qr.butterflymx.com/i/',
      'staging': 'https://viceroy-service-staging.platform-services-dev.butterflymx.com/i/',
      'development': 'https://viceroy-service-dev.platform-services-dev.butterflymx.com/i/'
    };

    const expectedBase = environmentUrls[environment];
    if (!expectedBase) {
      return { isValid: false, error: `Unknown environment: ${environment}` };
    }

    if (!trimmed.startsWith(expectedBase)) {
      return { isValid: false, error: `URL must start with ${expectedBase} for ${environment} environment` };
    }

    // Check that there's a QR identifier after the base
    const qrIdentifier = trimmed.substring(expectedBase.length);
    if (!qrIdentifier || qrIdentifier.trim().length === 0) {
      return { isValid: false, error: 'URL must contain a QR identifier after /i/' };
    }

    if (/\s/.test(qrIdentifier)) {
      return { isValid: false, error: 'QR identifier cannot contain whitespace' };
    }

    return { isValid: true };
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    // Trim the search value
    const trimmedSearchValue = searchValue.trim();

    if (!trimmedSearchValue) {
      setError(`Please enter a ${queryType === 'serial' ? 'serial number' : 'URL or QR identifier'}`);
      return;
    }

    // Validate input based on query type
    if (queryType === 'serial') {
      const serialValidation = validateSerialNumber(trimmedSearchValue);
      if (!serialValidation.isValid) {
        setError(serialValidation.error!);
        return;
      }
    } else {
      // For URL queries, allow both full URLs and QR identifiers
      // If it looks like a URL, validate it
      if (trimmedSearchValue.startsWith('http://') || trimmedSearchValue.startsWith('https://')) {
        const urlValidation = validateUrl(trimmedSearchValue, environment);
        if (!urlValidation.isValid) {
          setError(urlValidation.error!);
          return;
        }
      }
      // If it's just a QR identifier, we'll let the backend validate it
    }

    setLoading(true);
    try {
      let mapping: DeviceMapping | null = null;
      
      if (queryType === 'serial') {
        mapping = await apiService.getMapping(trimmedSearchValue, environment);
      } else {
        mapping = await apiService.getMappingByQR(trimmedSearchValue, environment);
      }
      
      if (mapping) {
        setResult(mapping);
        setEditImageCreated(mapping.imageCreated);
        setEditEtchCompleted(mapping.etchCompleted);
      } else {
        setError(`No mapping found for this ${queryType === 'serial' ? 'serial number' : 'URL/QR identifier'}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to query mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (result) {
      setEditImageCreated(result.imageCreated);
      setEditEtchCompleted(result.etchCompleted);
      setEditing(true);
      setError(null);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    if (result) {
      setEditImageCreated(result.imageCreated);
      setEditEtchCompleted(result.etchCompleted);
    }
  };

  const handleCameraOpen = () => {
    setShowCamera(true);
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  const handleCameraScan = async (qrUrl: string, serialNumber: string) => {
    setShowCamera(false);
    setScannedData({ qrUrl, serial: serialNumber });

    // Auto-populate fields and search
    setQueryType('url');
    setSearchValue(qrUrl);

    // Query database with scanned QR
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const mapping = await apiService.getMappingByQR(qrUrl, environment);

      if (mapping) {
        // Check if serial matches
        if (mapping.serialNumber === serialNumber) {
          setResult(mapping);
          setEditImageCreated(mapping.imageCreated);
          setEditEtchCompleted(mapping.etchCompleted);

          // If not etched, show archive dialog
          if (!mapping.etchCompleted) {
            setShowArchiveDialog(true);
          }
        } else {
          setError(`Serial number mismatch! Scanned: ${serialNumber}, Database: ${mapping.serialNumber}`);
        }
      } else {
        setError(`No mapping found for QR URL: ${qrUrl}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to query mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveComplete = async () => {
    setShowArchiveDialog(false);
    // Refresh the result to show updated etch status
    if (result) {
      try {
        const updatedMapping = await apiService.getMapping(result.serialNumber, environment);
        if (updatedMapping) {
          setResult(updatedMapping);
          setEditEtchCompleted(updatedMapping.etchCompleted);
        }
      } catch (err) {
        console.error('Failed to refresh mapping:', err);
      }
    }
  };

  const handleUpdate = async () => {
    if (!result) return;

    setUpdating(true);
    setError(null);

    const wasEtchCompleted = result.etchCompleted;
    const willBeEtchCompleted = editEtchCompleted;
    const shouldArchive = !wasEtchCompleted && willBeEtchCompleted;

    try {
      // Update the mapping first
      const response = await apiService.updateMapping(
        result.serialNumber,
        editImageCreated,
        editEtchCompleted,
        environment
      );

      // Update the result with the new data
      setResult(response.mapping);
      setEditing(false);
      setError(null);

      // If etch completed was changed from false to true, archive files
      if (shouldArchive && result.orientation) {
        try {
          console.log(`Auto-archiving files for ${result.serialNumber}...`);
          const archiveResult = await googleDriveService.archiveSerialFiles(
            result.serialNumber,
            result.orientation as 'portrait' | 'landscape'
          );

          if (archiveResult.archivedFiles.length > 0) {
            setError(null); // Clear any existing errors
            alert(`✅ Successfully archived ${archiveResult.archivedFiles.length} file(s) for serial ${result.serialNumber} to the archive folder.`);
          } else {
            console.log('No files found to archive');
          }

          if (archiveResult.errors.length > 0) {
            console.warn('Some files failed to archive:', archiveResult.errors);
          }
        } catch (archiveErr: any) {
          console.error('Auto-archiving failed:', archiveErr);
          // Don't show archive errors as main errors since the mapping update succeeded
          alert(`⚠️ Mapping updated successfully, but auto-archiving failed: ${archiveErr.message}`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update mapping');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="query-form">
      <h2>🔍 Query Device Mapping</h2>
      <form onSubmit={handleSearch}>
        <div className="form-group">
          <label htmlFor="queryType">🔄 Query Type:</label>
          <select
            id="queryType"
            value={queryType}
            onChange={(e) => {
              setQueryType(e.target.value as 'serial' | 'url');
              setSearchValue(''); // Clear input when switching types
              setError(null);
              setResult(null);
            }}
            disabled={loading}
          >
            <option value="serial">By Serial Number</option>
            <option value="url">By URL/QR Identifier</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="searchValue">
            {queryType === 'serial' ? '🏷️ Serial Number:' : '🌐 URL or QR Identifier:'}
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
            type="text"
            id="searchValue"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={
              queryType === 'serial'
                ? 'Enter a 4- or 5-character serial number'
                : 'Enter full URL or QR identifier'
            }
            disabled={loading}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleCameraOpen}
              disabled={loading}
              style={{
                padding: '8px 12px',
                backgroundColor: '#6600ff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                opacity: loading ? 0.6 : 1,
              }}
              title="Scan QR Code with Camera"
            >
              📷
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>
      {error && <div className="error">{error}</div>}
      {result && (
        <div className="result">
          <h3>Mapping Found:</h3>
          <p><strong>Serial Number:</strong> {result.serialNumber}</p>
          <p><strong>QR Identifier:</strong> {result.qrIdentifier}</p>
          <p><strong>Orientation:</strong> {result.orientation}</p>
          
          {!editing ? (
            <>
              <p><strong>Image Created:</strong> {result.imageCreated ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Etch Completed:</strong> {result.etchCompleted ? '✅ Yes' : '❌ No'}</p>
              <button type="button" onClick={handleStartEdit} className="edit-button">
                ✏️ Edit Status
              </button>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="editImageCreated"><strong>🖼️ Image Created:</strong></label>
                <select
                  id="editImageCreated"
                  value={editImageCreated ? 'true' : 'false'}
                  onChange={(e) => setEditImageCreated(e.target.value === 'true')}
                  disabled={updating}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="editEtchCompleted"><strong>✅ Etch Completed:</strong></label>
                <select
                  id="editEtchCompleted"
                  value={editEtchCompleted ? 'true' : 'false'}
                  onChange={(e) => setEditEtchCompleted(e.target.value === 'true')}
                  disabled={updating}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div className="edit-buttons">
                <button 
                  type="button" 
                  onClick={handleUpdate} 
                  disabled={updating}
                  className="save-button"
                >
                  {updating ? '💾 Saving...' : '💾 Save Changes'}
                </button>
                <button 
                  type="button" 
                  onClick={handleCancelEdit} 
                  disabled={updating}
                  className="cancel-button"
                >
                  ❌ Cancel
                </button>
              </div>
            </>
          )}
          
          {result.createdAt && <p><strong>Created At:</strong> {new Date(result.createdAt).toLocaleString()}</p>}
          {result.createdBy && <p><strong>Created By:</strong> {result.createdBy}</p>}
          {result.updatedAt && <p><strong>Updated At:</strong> {new Date(result.updatedAt).toLocaleString()}</p>}
          {result.updatedBy && <p><strong>Updated By:</strong> {result.updatedBy}</p>}
        </div>
      )}

      {/* Camera Scanner Modal */}
      {showCamera && (
        <CameraScanner
          onScanComplete={handleCameraScan}
          onClose={handleCameraClose}
        />
      )}

      {/* Archive Dialog */}
      {showArchiveDialog && result && scannedData && (
        <ArchiveDialog
          mapping={result}
          scannedSerial={scannedData.serial}
          environment={environment}
          apiService={apiService}
          onClose={() => setShowArchiveDialog(false)}
          onArchiveComplete={handleArchiveComplete}
        />
      )}
    </div>
  );
};